import React, { lazy, Suspense, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { AppLoading } from 'components/AppLoading';
import FloatingWidget from 'components/FloatingWidget';
import Header from 'components/Layout/Header';
import RenderMenuContent from 'components/RenderMenuContent';
import SideBar from 'components/SideBar';
import { Spin } from 'components/Spin';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useUser } from 'global/UserContext';
import { useSideBar } from 'hooks/useSideBar';
import { cn } from 'lib/utils';
import { useThemeContext } from 'providers/ThemeProvider';
import { logger } from 'utils/logger';
import { useIsDesktop } from 'utils/mediaQueries';

// Re-export PublicLayout for public/unauthenticated pages
export { PublicLayout, PublicLayoutLogo } from './PublicLayout';

// ===================================================================
// PHASE 6: CODE SPLITTING - Layout Components Lazy Loading 🚀
// ===================================================================
// ThirdLevelLayout is only loaded when needed (third-level menus)
// Expected: Reduces main bundle by lazy-loading third-level navigation
// ===================================================================

const ThirdLevelLayout = lazy(() => import('pages/ThirdLevelLayout'));

interface ILayout {
  children: React.ReactNode | React.ReactNode[];
}

const Layout = ({ children }: ILayout) => {
  const { t } = useTranslation('common');
  const { currentLevel, firstLevelMenu, secondLevelMenu, thirdLevelMenu, activeDocumentId } =
    useMenuSelection();

  const { open } = useSideBar();
  const { isLoading } = useUser();
  const isDesktop = useIsDesktop();

  const theme = useThemeContext();
  const location = useLocation();

  // Check if the theme is loaded correctly
  const [themeError, setThemeError] = useState(false);
  useEffect(() => {
    if (!isLoading && !theme) {
      setThemeError(true);
    }
  }, [theme, isLoading]);

  // Log for layout debug
  useEffect(() => {
    logger.debug('[Layout] Estado atual:', {
      currentLevel,
      firstLevelMenu: firstLevelMenu?.label,
      secondLevelMenu: secondLevelMenu?.label,
      thirdLevelMenu: thirdLevelMenu?.label,
      activeDocumentId,
      path: location.pathname,
    });
  }, [
    currentLevel,
    firstLevelMenu,
    secondLevelMenu,
    thirdLevelMenu,
    activeDocumentId,
    location.pathname,
  ]);

  if (!theme) return null;

  if (themeError) {
    return (
      <div className="min-h-screen w-full bg-background-secondary">
        <div className="relative min-h-screen w-full overflow-x-hidden bg-background">
          <div className="mt-10 text-center">
            <h2>{t('theme.loadError')}</h2>
            <p>{t('theme.loadErrorDescription')}</p>
            <button onClick={() => window.location.reload()}>{t('theme.reloadPage')}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-background-secondary">
      <div className="relative min-h-screen w-full overflow-x-hidden bg-background">
        <>
          {isDesktop && (
            <>
              <SideBar />
              <Header />
              <>
                {isLoading ? (
                  <div className="flex h-screen w-full items-center justify-center">
                    <Spin centerHorizontally centerVertically />
                  </div>
                ) : (
                  <>
                    {currentLevel === 'first' && (
                      <div
                        className={cn(
                          'relative flex h-[calc(100%-56px)] w-full overflow-y-auto bg-background pb-4 pt-2 transition-[padding-left] duration-300',
                          open ? 'pl-60' : 'pl-16'
                        )}
                      >
                        <div className="flex h-full w-full bg-background px-6">
                          <RenderMenuContent menu={firstLevelMenu}>{children}</RenderMenuContent>
                        </div>
                      </div>
                    )}
                    {currentLevel === 'second' && (
                      <div
                        className={cn(
                          'relative flex h-[calc(100%-56px)] w-full overflow-y-auto bg-background pb-4 pt-2 transition-[padding-left] duration-300',
                          open ? 'pl-60' : 'pl-16'
                        )}
                      >
                        <div className="flex h-full w-full bg-background px-6">
                          <RenderMenuContent menu={secondLevelMenu}>{children}</RenderMenuContent>
                        </div>
                      </div>
                    )}
                    {currentLevel === 'third' && (
                      <div
                        className={cn(
                          'relative flex h-[calc(100%-56px)] w-full overflow-y-auto bg-background pb-4 pt-2 transition-[padding-left] duration-300',
                          open ? 'pl-60' : 'pl-16'
                        )}
                      >
                        <div className="flex h-full w-full bg-background px-6">
                          <Suspense fallback={<AppLoading />}>
                            <ThirdLevelLayout>{children}</ThirdLevelLayout>
                          </Suspense>
                        </div>
                      </div>
                    )}
                    {!currentLevel && (
                      <div
                        className={cn(
                          'relative flex h-[calc(100%-56px)] w-full overflow-y-auto bg-background pb-4 pt-2 transition-[padding-left] duration-300',
                          open ? 'pl-60' : 'pl-16'
                        )}
                      >
                        <div className="flex h-full w-full bg-background px-6">{children}</div>
                      </div>
                    )}
                  </>
                )}
                <FloatingWidget />
              </>
            </>
          )}
        </>
      </div>
    </div>
  );
};

export default Layout;
